/**
 * Location Hooks
 *
 * TanStack Query hooks for location management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import type {
  Location,
  LocationListResponse,
  CreateLocationRequest,
  UpdateLocationRequest,
} from '../types/location';

/** Query keys for locations */
export const locationKeys = {
  all: ['locations'] as const,
  list: () => [...locationKeys.all, 'list'] as const,
  detail: (id: string) => [...locationKeys.all, 'detail', id] as const,
};

/**
 * Fetch all locations
 */
export function useLocations() {
  return useQuery({
    queryKey: locationKeys.list(),
    queryFn: () => apiFetch<LocationListResponse>('/api/locations'),
    staleTime: 60_000, // 1 minute - locations change rarely
  });
}

/**
 * Create new location
 */
export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLocationRequest) =>
      apiFetch<Location>('/api/locations', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.list() });
    },
  });
}

/**
 * Update existing location
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateLocationRequest & { id: string }) =>
      apiFetch<Location>(`/api/locations/${id}`, {
        method: 'PUT',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.list() });
    },
  });
}

/**
 * Delete location
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/locations/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.list() });
    },
  });
}
